import { createFileRoute } from '@tanstack/react-router';
import type { Event } from '@/features/events/types';
import { useEffect, useState } from 'react';
import React from 'react';
import { auth } from '@/core/firebase/config';
import { OfficeSelector } from '@/features/office/components/OfficeSelector';
import useGetPreferredOffice from '@/shared/hooks/useGetPreferredOffice';
import useGetEvents from '@/features/events/hooks/useGetEvents';
import useSaveEvent from '@/features/events/hooks/useSaveEvent';
import useDeleteEvent from '@/features/events/hooks/useDeleteEvent';
import useGetOfficeList from '@/features/office/hooks/useGetOfficeList';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import { Navigate, useLocation } from '@tanstack/react-router';

import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog';
import { SimpleDatePicker } from '@/shared/components/SimpleDatePicker';
import { DeleteEventDialog } from '@/features/admin/components/administration/Events/DeleteEventDialog';
import { AdminContentSkeleton } from '@/shared/components/feedback/skeletons';
import { format } from 'date-fns';
import { Calendar, FileText, ChevronDown } from 'lucide-react';
import { isHttpUrl } from '@/shared/utils/url';
import { IconPlus, IconCalendarEvent, IconEdit, IconTrash } from '@tabler/icons-react';
import { AdminHeader, AdminPageLayout } from '@/shared/components/ui/AdminHeader';
import { ColumnDef, Row } from '@tanstack/react-table';
import { Badge } from '@/shared/components/ui/badge';
import { DataTable } from '@/shared/components/ui/DataTable';
import { EmptyState, EmptyStateIcons } from '@/shared/components/ui/EmptyState';
import { toast } from 'sonner';

export const Route = createFileRoute('/admin/events')({
  component: RouteComponent,
});

function RouteComponent() {
  const { user, loading: authLoading, roleReady } = useAuth();
  const location = useLocation();

  const { preferredOffice } = useGetPreferredOffice(auth.currentUser?.uid);
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  // Delete modal state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);

  const { Events, loading: eventsLoading } = useGetEvents(selectedOfficeId);
  const saveEvent = useSaveEvent();
  const deleteEvent = useDeleteEvent();

  const officeList = useGetOfficeList();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    locationUrl: '',
    startDate: new Date(),
    endDate: new Date(),
  });

  useEffect(() => {
    // Set preferred office if available and no office is selected
    if (preferredOffice && !selectedOfficeId) {
      setSelectedOfficeId(preferredOffice);
    }
  }, [preferredOffice, selectedOfficeId]);

  if (authLoading || !roleReady) return null;

  if (!user) {
    return <Navigate to="/login" search={{ from: location.pathname }} replace />;
  }

  if (user.role !== 'admin' && user.role !== 'officeManager') {
    return <Navigate to="/" replace />;
  }

  const resetFormData = () => {
    const today = new Date();
    setFormData({
      title: '',
      description: '',
      locationUrl: '',
      startDate: today,
      endDate: today,
    });
    setEditingEvent(null);
  };

  const handleOpenDialog = (event?: Event) => {
    if (event) {
      setEditingEvent(event);
      const startDate = new Date(event.startDate);
      setFormData({
        title: event.title,
        description: event.description || '',
        locationUrl: event.locationUrl || '',
        startDate: startDate,
        endDate: event.endDate ? new Date(event.endDate) : startDate,
      });
    } else {
      resetFormData();
    }
    setDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) {
      // If opening for a new event (not editing), reset form data
      if (!editingEvent) {
        resetFormData();
      }
      setDialogOpen(true);
    } else {
      setDialogOpen(false);
      resetFormData();
    }
  };

  const handleSave = async () => {
    if (!selectedOfficeId || !formData.title || !formData.startDate) return;

    const trimmedLocationUrl = formData.locationUrl.trim();
    if (trimmedLocationUrl && !isHttpUrl(trimmedLocationUrl)) {
      toast.error('Location link must be a valid http or https URL');
      return;
    }

    try {
      const eventData: {
        title: string;
        description: string;
        startDate: string;
        createdBy: string;
        endDate?: string;
        locationUrl?: string;
      } = {
        title: formData.title,
        description: formData.description,
        startDate: format(formData.startDate, 'yyyy-MM-dd'),
        createdBy: auth.currentUser?.displayName || '',
      };

      // Only include endDate if it's different from startDate
      if (format(formData.endDate, 'yyyy-MM-dd') !== format(formData.startDate, 'yyyy-MM-dd')) {
        eventData.endDate = format(formData.endDate, 'yyyy-MM-dd');
      }

      if (trimmedLocationUrl) {
        eventData.locationUrl = trimmedLocationUrl;
      }

      await saveEvent(selectedOfficeId, eventData, editingEvent?.id);
      handleDialogOpenChange(false);
      toast.success('Event saved successfully');
    } catch (error) {
      console.error('Failed to save event:', error);
      toast.error('Failed to save event');
    }
  };

  const handleDelete = (event: Event) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedOfficeId || !eventToDelete) return;

    try {
      await deleteEvent(selectedOfficeId, eventToDelete.id);
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      toast.success('Event deleted successfully');
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    }
  };

  const currentOfficeHasEventsDisabled = (() => {
    if (!selectedOfficeId) return false;
    const office = officeList.find(o => o.id === selectedOfficeId);
    return office?.layout?.enableEvents === false;
  })();

  // Define columns
  const columns: ColumnDef<Event>[] = [
    {
      accessorKey: 'title',
      header: () => (
        <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
          <IconCalendarEvent className="size-4" />
          Event Title
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div>
            <div className="font-semibold text-slate-900 dark:text-slate-100">
              {row.getValue('title')}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">ID: {row.original.id}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'startDate',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 hover:bg-transparent font-semibold text-slate-700 dark:text-slate-300"
          >
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              Dates
              <ChevronDown className="size-3 ml-1" />
            </div>
          </Button>
        );
      },
      cell: ({ row }) => {
        const startDate = row.getValue('startDate') as string;
        const endDate = row.original.endDate;

        return (
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/40"
            >
              {format(new Date(startDate), 'MMM d, yyyy')}
              {endDate && ` - ${format(new Date(endDate), 'MMM d, yyyy')}`}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: 'description',
      header: () => (
        <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
          <FileText className="size-4" />
          Description
        </div>
      ),
      cell: ({ row }) => {
        const description = row.getValue('description') as string;
        return (
          <div className="max-w-sm">
            {description ? (
              <div
                className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 leading-relaxed"
                title={description}
              >
                {description}
              </div>
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-500 italic">
                No description
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'createdBy',
      header: () => (
        <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
          Created By
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className="bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800/40"
          >
            {row.getValue('createdBy') as string}
          </Badge>
        </div>
      ),
    },
    {
      id: 'actions',
      header: () => (
        <div className="flex items-center justify-end">
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Actions</span>
        </div>
      ),
      enableHiding: false,
      cell: ({ row }) => {
        const event = row.original;

        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleOpenDialog(event)}
              className="h-8 w-8 p-0 hover:bg-blue-100 dark:hover:bg-blue-950/40 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-200"
              aria-label="Edit event"
              title="Edit event"
            >
              <IconEdit
                size={16}
                className="text-slate-600 dark:text-slate-400 hover:text-blue-700 dark:hover:text-blue-400"
              />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(event)}
              className="h-8 w-8 p-0 hover:bg-red-100 dark:hover:bg-red-950/40 hover:text-red-700 dark:hover:text-red-400 transition-colors duration-200"
              aria-label="Delete event"
              title="Delete event"
            >
              <IconTrash
                size={16}
                className="text-slate-600 dark:text-slate-400 hover:text-red-700 dark:hover:text-red-400"
              />
            </Button>
          </div>
        );
      },
    },
  ];

  const actionButton = (
    <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
      <DialogTrigger asChild>
        <Button
          className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 w-full sm:w-auto"
          disabled={!selectedOfficeId || currentOfficeHasEventsDisabled}
        >
          <IconPlus className="size-4 mr-2" />
          Add Event
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg overflow-visible">
        <DialogHeader>
          <DialogTitle>{editingEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
          <DialogDescription>
            {editingEvent
              ? 'Update the event details below.'
              : 'Fill in the details to create a new event.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder="Event title"
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Event description (optional)"
              rows={3}
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="locationUrl">Location link</Label>
            <Input
              id="locationUrl"
              type="url"
              value={formData.locationUrl}
              onChange={e => setFormData({ ...formData, locationUrl: e.target.value })}
              placeholder="https://maps.google.com/..."
              className="mt-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="relative overflow-visible">
              <Label>Start Date *</Label>
              <div className="mt-2">
                <SimpleDatePicker
                  currentDate={formData.startDate}
                  onDateChange={date => {
                    // If the new start date is after the end date, update end date to match start date
                    const newEndDate = date > formData.endDate ? date : formData.endDate;
                    setFormData({ ...formData, startDate: date, endDate: newEndDate });
                  }}
                  dateFormat="MMM d, yyyy"
                />
              </div>
            </div>

            <div className="relative overflow-visible">
              <Label>End Date (optional)</Label>
              <div className="mt-2">
                <SimpleDatePicker
                  currentDate={formData.endDate}
                  onDateChange={date => {
                    setFormData({ ...formData, endDate: date });
                  }}
                  dateFormat="MMM d, yyyy"
                  disabledDates={date => date < formData.startDate}
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleDialogOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!formData.title.trim()}>
            {editingEvent ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const emptyState = (
    <EmptyState
      icon={EmptyStateIcons.Events}
      title="No events yet"
      description="Create your first event to get started."
      action={
        <Button
          onClick={() => handleOpenDialog()}
          variant="outline"
          className="border-purple-200 text-purple-700 hover:bg-purple-50"
        >
          <IconPlus className="size-4 mr-2" />
          Create Event
        </Button>
      }
    />
  );

  const officeSelector = (
    <OfficeSelector
      selectedValue={selectedOfficeId}
      onChange={setSelectedOfficeId}
      showEventsDisabled={true}
      autoSelectFirst={false}
      filterByManagedOffices={true}
    />
  );

  const header = (
    <AdminHeader
      icon={IconCalendarEvent}
      title="Events Management"
      description="Create and manage events for office locations to keep your team informed"
      rightContent={officeSelector}
      variant="purple"
    />
  );

  const mobileRowRenderer = (row: Row<Event>) => {
    const event = row.original;
    const startDate = row.getValue('startDate') as string;
    const endDate = event.endDate;

    return (
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="size-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
              <IconCalendarEvent className="size-5" />
            </div>
            <div className="space-y-1 min-w-0">
              <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                {event.title}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Calendar className="size-3" />
                <span>
                  {format(new Date(startDate), 'MMM d, yyyy')}
                  {endDate && ` - ${format(new Date(endDate), 'MMM d, yyyy')}`}
                </span>
              </div>
              {event.description && (
                <div className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                  {event.description}
                </div>
              )}
              <div className="pt-1">
                <Badge
                  variant="secondary"
                  className="bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800/40 text-[10px] px-1.5 h-5"
                >
                  By: {event.createdBy}
                </Badge>
              </div>
            </div>
          </div>

          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleOpenDialog(event)}
              className="h-8 w-8 text-slate-500 dark:text-slate-400 hover:text-blue-700 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40"
              aria-label="Edit event"
              title="Edit event"
            >
              <IconEdit className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(event)}
              className="h-8 w-8 text-slate-500 dark:text-slate-400 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
              aria-label="Delete event"
              title="Delete event"
            >
              <IconTrash className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminPageLayout header={header}>
      {/* Events List */}
      {selectedOfficeId ? (
        currentOfficeHasEventsDisabled ? (
          <div className="bg-white dark:bg-slate-800 rounded-lg border-slate-200 dark:border-slate-700 shadow-sm">
            <EmptyState
              icon={EmptyStateIcons.Events}
              title="Events Disabled"
              description="Event management is currently disabled for this office. You can enable it in the office layout settings."
            />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={Events}
            searchPlaceholder="Search events..."
            searchClassName="border-slate-200 dark:border-slate-600 focus:border-purple-500 focus:ring-purple-500/20"
            actionButton={actionButton}
            emptyState={emptyState}
            itemName="events"
            getRowId={row => row.id}
            loading={eventsLoading}
            loadingComponent={<AdminContentSkeleton />}
            className="space-y-6"
            mobileRowRenderer={mobileRowRenderer}
          />
        )
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-lg border-slate-200 dark:border-slate-700 shadow-sm">
          <EmptyState
            icon={EmptyStateIcons.Building}
            title="No Office Selected"
            description="Please select an office from the dropdown to manage its events. If no offices are available, it might be because none have events enabled."
          />
        </div>
      )}

      {/* Delete Event Modal */}
      <DeleteEventDialog
        open={deleteDialogOpen}
        event={eventToDelete}
        onConfirm={confirmDelete}
        onOpenChange={() => setDeleteDialogOpen(false)}
      />
    </AdminPageLayout>
  );
}
